/**
 * Enumerations for MedDream hangingProtocolsV2 (document version 8.9.0).
 *
 * Values were read out of MedDream's own bundle (md.main.min.js) rather than
 * inferred from UI labels — several do not match their label at all, e.g. the
 * "Fit to image" button stores `zoom_to_image`, and "Menu" stores `manual`.
 * Labels below are MedDream's English strings, kept for UI parity.
 */

/** applyConfiguration.* — Auto / Menu / No. */
export const APPLY_MODE = {
  AUTO: "auto",
  MENU: "manual",
  NO: "no",
};

export const APPLY_MODE_OPTIONS = [
  { value: APPLY_MODE.AUTO, label: "Auto" },
  { value: APPLY_MODE.MENU, label: "Menu" },
  { value: APPLY_MODE.NO, label: "No" },
];

/** category.linkScrollSeries. NONE is the unset state a new category starts in. */
export const LINK_SCROLL_SERIES = {
  NONE: "NONE",
  OFF: "OFF",
  MANUAL: "MANUAL",
  AUTO: "AUTO",
  POSITION: "POSITION",
};

export const LINK_SCROLL_SERIES_OPTIONS = [
  { value: LINK_SCROLL_SERIES.OFF, label: "Non-synchronize" },
  { value: LINK_SCROLL_SERIES.MANUAL, label: "Manual synchronize" },
  { value: LINK_SCROLL_SERIES.AUTO, label: "Auto synchronize" },
  { value: LINK_SCROLL_SERIES.POSITION, label: "Distance synchronize" },
];

/** category.syncActions. */
export const SYNC_ACTIONS = {
  NONE: "NONE",
  OFF: "OFF",
  MANUAL: "MANUAL",
  SERIES: "SERIES",
  LINKED: "LINKED",
};

export const SYNC_ACTIONS_OPTIONS = [
  { value: SYNC_ACTIONS.OFF, label: "Sync actions disabled" },
  { value: SYNC_ACTIONS.MANUAL, label: "All Sync in all viewports" },
  { value: SYNC_ACTIONS.SERIES, label: "All Sync for same series" },
  { value: SYNC_ACTIONS.LINKED, label: "All Sync in linked series" },
];

/** viewport.mprMist. */
export const MPR_MIST = {
  DISABLED: "disabled",
  OBLIQUE: "oblique",
  AXIAL: "axial",
  CORONAL: "coronal",
  SAGITTAL: "sagittal",
  DENTAL: "dental",
  PET: "pet",
};

export const MPR_MIST_OPTIONS = [
  { value: MPR_MIST.DISABLED, label: "DISABLED" },
  { value: MPR_MIST.OBLIQUE, label: "OBLIQUE" },
  { value: MPR_MIST.AXIAL, label: "AXIAL" },
  { value: MPR_MIST.CORONAL, label: "CORONAL" },
  { value: MPR_MIST.SAGITTAL, label: "SAGITTAL" },
  { value: MPR_MIST.DENTAL, label: "DENTAL" },
  { value: MPR_MIST.PET, label: "PET" },
];

/** viewport.mprSeriesInViewer. */
export const MPR_SERIES_IN_VIEWER = {
  NO: "no",
  ACTIVE: "active",
  ENLARGED_1: "enlarged1",
  ENLARGED_2: "enlarged2",
};

export const MPR_SERIES_IN_VIEWER_OPTIONS = [
  { value: MPR_SERIES_IN_VIEWER.NO, label: "No" },
  { value: MPR_SERIES_IN_VIEWER.ACTIVE, label: "Active" },
  { value: MPR_SERIES_IN_VIEWER.ENLARGED_1, label: "Enlarged 1" },
  { value: MPR_SERIES_IN_VIEWER.ENLARGED_2, label: "Enlarged 2" },
];

/** viewport.scale. Note the "Fit to image" label maps to `zoom_to_image`. */
export const SCALE = {
  ORIGINAL: "original",
  FIT_TO_SCREEN: "fit_to_screen",
  ZOOM_TO_IMAGE: "zoom_to_image",
  ZOOM: "zoom",
};

export const SCALE_OPTIONS = [
  { value: SCALE.ORIGINAL, label: "Original" },
  { value: SCALE.FIT_TO_SCREEN, label: "Fit to screen" },
  { value: SCALE.ZOOM_TO_IMAGE, label: "Fit to image" },
  { value: SCALE.ZOOM, label: "Zoom" },
];

/** viewport.alignment. The *AndLock variants pin panning as well as position. */
export const ALIGNMENT = {
  RIGHT: "right",
  RIGHT_AND_LOCK: "rightAndLock",
  LEFT: "left",
  LEFT_AND_LOCK: "leftAndLock",
  CENTER: "center",
};

export const ALIGNMENT_OPTIONS = [
  { value: ALIGNMENT.RIGHT, label: "Right" },
  { value: ALIGNMENT.RIGHT_AND_LOCK, label: "Right & Lock" },
  { value: ALIGNMENT.LEFT, label: "Left" },
  { value: ALIGNMENT.LEFT_AND_LOCK, label: "Left & Lock" },
  { value: ALIGNMENT.CENTER, label: "Center" },
];

/** group.devices — an empty array means the group applies to every device. */
export const DEVICE = {
  MOBILE: "mobile",
  TABLET: "tablet",
  DESKTOP_1: "desktop_1_disp",
  DESKTOP_2: "desktop_2_disp",
  DESKTOP_3: "desktop_3_disp",
  DESKTOP_4: "desktop_4_disp",
};

export const DEVICE_OPTIONS = [
  { value: DEVICE.MOBILE, label: "Mobile" },
  { value: DEVICE.TABLET, label: "Tablet" },
  { value: DEVICE.DESKTOP_1, label: "Desktop, 1 display" },
  { value: DEVICE.DESKTOP_2, label: "Desktop, 2 displays" },
  { value: DEVICE.DESKTOP_3, label: "Desktop, 3 displays" },
  { value: DEVICE.DESKTOP_4, label: "Desktop, 4 displays" },
];

/** Top-level collections; a group lives in exactly one of them. */
export const GROUP_KIND = {
  STANDARD: "hp",
  DRAG: "hpDrag",
  COMPARISON: "hpComparison",
};

/** Prefix MedDream uses when referring to a group from a dropdown. */
export const GROUP_KIND_REF_PREFIX = {
  [GROUP_KIND.STANDARD]: "standard",
  [GROUP_KIND.DRAG]: "drag",
  [GROUP_KIND.COMPARISON]: "comparison",
};

/**
 * Who a group belongs to, and the key it arrives under.
 *
 * Not MedDream's — the backend stamps it on each group of the *effective* document alone
 * (`/hpv2/document/effective`), which is the read our own viewer takes. Authoring exports
 * and the file delivered to the Advance Viewer carry no such key, so treat a missing value
 * as "unknown" rather than as either scope: a share-link visitor, for one, is served the
 * shared set unstamped.
 */
export const GROUP_SCOPE_KEY = "_scope";

export const GROUP_SCOPE = {
  /** The radiologist's own protocols. */
  USER: "USER",
  /** The tenant's shared set, authored by an admin. */
  SYSTEM: "SYSTEM",
};

export const DOCUMENT_VERSION = "8.9.0";

const valuesOf = (obj) => Object.values(obj);

export const ENUM_VALUES = {
  applyMode: valuesOf(APPLY_MODE),
  linkScrollSeries: valuesOf(LINK_SCROLL_SERIES),
  syncActions: valuesOf(SYNC_ACTIONS),
  mprMist: valuesOf(MPR_MIST),
  mprSeriesInViewer: valuesOf(MPR_SERIES_IN_VIEWER),
  scale: valuesOf(SCALE),
  alignment: valuesOf(ALIGNMENT),
  device: valuesOf(DEVICE),
};
